import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Custom upholstered homosote pin board — per Quoting_markdown/pin_boards.md.
// Submitted through dedicated addPinBoardDraftQuoteObject/
// updatePinBoardDraftQuoteObject mutations (see DraftQuoteDetail.tsx's
// DEDICATED_TYPE_CONFIG/handleAddObject), same deviation Cornice/Bed/Bench
// established.
//
// Width is stored in width_inches and Length in height_inches (the SOP's
// "Length" is the other dimension), so existing generic columns carry both.

type Mounting = 'none' | 'velcro' | 'french_cleat';

const PinBoardForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [length, setLength] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricWidth, setFabricWidth] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [patternRepeat, setPatternRepeat] = useState(iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '');
  const [frame, setFrame] = useState<boolean>(iv?.pinBoardFrame || false);
  const [framePaintColor, setFramePaintColor] = useState<string>(iv?.pinBoardFramePaintColor || '');
  const [ribbonNailheads, setRibbonNailheads] = useState<boolean>(iv?.pinBoardRibbonNailheads || false);
  const [trimDescription, setTrimDescription] = useState<string>(iv?.trimDescription || '');
  const [nailheadColor, setNailheadColor] = useState<string>(iv?.nailheadColor || '');
  const [mounting, setMounting] = useState<Mounting>((iv?.pinBoardMounting as Mounting) || 'none');
  const [measureTemplate, setMeasureTemplate] = useState<boolean>(iv?.pinBoardMeasureTemplate || false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!width) { setError('Width is required'); return null; }
      if (!length) { setError('Length is required'); return null; }
      setError(null);
      return {
        widthInches: parseFloat(width),
        heightInches: parseFloat(length),
        fabricDescription: fabricDescription.trim() || null,
        fabricWidthInches: fabricWidth ? parseFloat(fabricWidth) : null,
        patternRepeatInches: patternRepeat ? parseFloat(patternRepeat) : null,
        pinBoardFrame: frame,
        pinBoardFramePaintColor: frame ? (framePaintColor.trim() || null) : null,
        pinBoardRibbonNailheads: ribbonNailheads,
        trimDescription: ribbonNailheads ? (trimDescription.trim() || null) : null,
        nailheadColor: ribbonNailheads ? (nailheadColor.trim() || null) : null,
        pinBoardMounting: mounting,
        pinBoardMeasureTemplate: measureTemplate,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Width (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={width}
            onChange={(e) => setWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Length (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={length}
            onChange={(e) => setLength(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ width: 120 }}>
          <label>Fabric Width (in)</label>
          <input type="number" min="0" step="1" className="input" value={fabricWidth}
            onChange={(e) => setFabricWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label>Pattern Repeat (in) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="number" min="0" step="0.25" className="input" value={patternRepeat}
            onChange={(e) => setPatternRepeat(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={frame}
              onChange={(e) => setFrame(e.target.checked)} disabled={props.disabled} />
            {' '}With Frame <span style={{ color: '#9ca3af', fontWeight: 400 }}>(+$2/in of perimeter)</span>
          </label>
        </div>
        {frame && (
          <div className="form-group" style={{ flex: 2 }}>
            <label>Frame Paint Color</label>
            <input type="text" className="input" value={framePaintColor}
              onChange={(e) => setFramePaintColor(e.target.value)} disabled={props.disabled} />
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={ribbonNailheads}
              onChange={(e) => setRibbonNailheads(e.target.checked)} disabled={props.disabled} />
            {' '}With diagonal ribbon trim and nailheads <span style={{ color: '#9ca3af', fontWeight: 400 }}>($8/yd, includes both)</span>
          </label>
        </div>
      </div>
      {ribbonNailheads && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Trim Name</label>
            <input type="text" className="input" value={trimDescription}
              onChange={(e) => setTrimDescription(e.target.value)} disabled={props.disabled} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Nailhead Color</label>
            <input type="text" className="input" value={nailheadColor}
              onChange={(e) => setNailheadColor(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Mounting</label>
          <select className="input" value={mounting}
            onChange={(e) => setMounting(e.target.value as Mounting)} disabled={props.disabled}>
            <option value="none">None</option>
            <option value="velcro">Sticky-back Velcro ($20/yd)</option>
            <option value="french_cleat">French Cleat (+$150)</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={measureTemplate}
              onChange={(e) => setMeasureTemplate(e.target.checked)} disabled={props.disabled} />
            {' '}Measure &amp; Template <span style={{ color: '#9ca3af', fontWeight: 400 }}>(+$125, custom shape)</span>
          </label>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default PinBoardForm;
